import {
  type ValidateQBankStructureBeforePublishQuery,
  QBankStructureStatus,
  QBankStructureValidationCase,
  useGetQBankStructureLevelItemsByQBankStructureIdLazyQuery,
  useValidateQBankStructureBeforePublishLazyQuery,
} from '@exam-management/graphql';
import { type QBankStructureLevelItem, useQBankStructure } from '@exam-management/qbs-provider';
import {
  type DialogProps,
  CloseRoundedIcon,
  Dialog,
  DialogTitle,
  IconButton,
} from '@shared/components';

import { PublishConfirmationSection } from '../../components';

interface ValidateAndPublishModalProps extends DialogProps {
  onClose?: () => void;
  onPublish: () => Promise<void>;
  loading: boolean;
}

export default function ValidateAndPublishModal(props: Readonly<ValidateAndPublishModalProps>) {
  const { onClose, onPublish, open: isOpen, loading: isLoading, ...restProps } = props;
  const {
    qBankStructureInformation,
    qBankStructureLevelItems,
    formMethodsRef,
    validatePublishQBankStructure,
  } = useQBankStructure();

  const handleClose = () => {
    onClose?.();
  };

  const [getLevelItems] = useGetQBankStructureLevelItemsByQBankStructureIdLazyQuery({
    variables: {
      qBankStructureId: qBankStructureInformation.id,
      options: null,
    },
    fetchPolicy: 'cache-first',
  });

  const [validateQBankStructureBeforePublish, { loading: isValidating }] =
    useValidateQBankStructureBeforePublishLazyQuery({
      fetchPolicy: 'network-only',
    });

  const handleValidate = (data: ValidateQBankStructureBeforePublishQuery | undefined) => {
    if (data?.qbsValidateQBankStructureBeforePublish.every((result) => result.passed)) {
      onPublish();
    } else {
      const errors =
        data?.qbsValidateQBankStructureBeforePublish.flatMap((result) =>
          result.issues.map((issue) => ({
            name: issue.levelItemId ?? '',
            type: result.case,
          })),
        ) ?? [];

      const missingLastItemErrors = errors.filter(
        (error) => error.type === QBankStructureValidationCase.MissingLastLevelItem,
      );

      validatePublishQBankStructure.setErrors(errors);
      validatePublishQBankStructure.setMissingLastItemErrors(missingLastItemErrors);
    }
  };

  const validateQBS = async (currentLevelItems: QBankStructureLevelItem[]) => {
    const levelItems = currentLevelItems.map(
      ({ id, order, parentLevelItemId, qBankStructureLevelId, tag }) => ({
        id,
        order,
        parentLevelItemId,
        qBankStructureLevelId,
        tag: tag ? { name: tag.name } : null,
      }),
    );

    validateQBankStructureBeforePublish({
      variables: {
        input: {
          id: qBankStructureInformation.id,
          levelItems: levelItems ?? [],
        },
      },
    }).then(({ data }) => {
      handleValidate(data);
      handleClose();
    });
  };

  const handleSubmit = () => {
    const { formContext, onSubmit } = formMethodsRef.current ?? {};
    if (formContext && onSubmit) {
      formContext.handleSubmit(async (data) => {
        const submissionResponse = await onSubmit(data);
        if (submissionResponse) {
          const result = await getLevelItems();
          const data = result.data?.qbsGetQBankStructureLevelItemsByQBankStructureId ?? [];
          validateQBS(data);
        }
      })();
    } else {
      validateQBS(qBankStructureLevelItems);
    }
  };

  const isSubmitting = formMethodsRef.current?.formContext.formState.isSubmitting;
  const shouldDisableClose = isLoading || isValidating || isSubmitting;

  const isDraft = qBankStructureInformation.status === QBankStructureStatus.Draft;
  const title = isDraft ? 'Publish QBank Structure?' : 'อัปเดตโครงสร้างหลักสูตร';

  return (
    <Dialog open={isOpen} {...restProps} maxWidth='xs'>
      <DialogTitle>{title}</DialogTitle>
      <IconButton
        onClick={onClose}
        disabled={shouldDisableClose}
        aria-label='close'
        sx={(theme) => ({
          position: 'absolute',
          right: 16,
          top: 16,
          color: theme.palette.text.primary,
        })}
      >
        <CloseRoundedIcon />
      </IconButton>
      <PublishConfirmationSection
        draft={isDraft}
        loading={shouldDisableClose}
        onSubmit={handleSubmit}
        onClose={handleClose}
      />
    </Dialog>
  );
}
